import styled from '@emotion/styled';

import { useCallback } from 'react';

import COLOR from '../../../../../shared/constant/colorset';

import { addComma } from '../../../../../shared/services';

import { Question21x21cccc } from '../../../../../shared/images';
import PriceInformationAlert from '../../../shared/alert/PriceInformationAlert';
import { PriceService } from '../../../../service/priceService';
import { amplitudeService } from '../../../../../shared/amplitude';
import { SelectPriceTooltipButton } from '../../../../../shared/amplitude/events/product/SelectPriceTooltipButton';
import { getFusionQueryId } from '../../../shared/utils/productDetailEvent';
import { useAppSelector } from '../../../../../shared/store';

const PriceWrapper = styled.h2`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  padding-top: 19px;
  font-weight: bold;
  line-height: 30px;
  letter-spacing: -0.5px;
`;

const DiscountPercent = styled.span`
  padding-right: 9px;
  font-size: 28px;
  color: ${COLOR.pointText};
`;

const PriceValue = styled.span`
  padding-right: 4px;
  font-size: 28px;
  color: ${COLOR.kurlyGray800};
`;

const PriceSubText = styled.span`
  display: inline-block;
  position: relative;
  top: 3px;
  font-size: 18px;
  color: ${COLOR.kurlyGray800};
  vertical-align: top;
`;

const SubPriceText = styled.span<{ lineThrough: boolean }>`
  display: inline-block;
  font-size: 16px;
  color: ${COLOR.kurlyGray400};
  letter-spacing: -0.5px;
  padding-top: 9px;
  ${({ lineThrough }) =>
    lineThrough ? 'text-decoration: line-through; margin-right: 0;' : 'text-decoration: none; margin-right: 4px;'}
`;

const QuestionIcon = styled.img`
  width: 21px;
  height: 21px;
  vertical-align: -4px;
  padding-left: 1px;
  cursor: pointer;
`;

interface SubPriceProps {
  lineThrough?: boolean;
  onClickPriceInformation: () => void;
  subPrice: number | null;
}

const SubPrice = ({ lineThrough = true, onClickPriceInformation, subPrice }: SubPriceProps) => {
  if (!subPrice) {
    return null;
  }

  return (
    <SubPriceText lineThrough={lineThrough} onClick={onClickPriceInformation}>
      {addComma(subPrice)}원{lineThrough && <QuestionIcon src={Question21x21cccc} alt="컬리판매가 자세히 보기" />}
    </SubPriceText>
  );
};

interface Props {
  discountRate: number;
  discountedPrice: number | null;
  basePrice: number;
  retailPrice: number | null;
  isMultiplePrice: boolean;
}

export default function Price({ discountRate, discountedPrice, basePrice, retailPrice, isMultiplePrice }: Props) {
  const { queryId } = useAppSelector(({ productList }) => productList);

  const { representativePrice, kurlyPrice, originalPrice } = new PriceService({
    retailPrice,
    basePrice,
    discountedPrice,
  });

  const lineThrough = !!kurlyPrice && !originalPrice;

  const handleClickPriceInformation = useCallback(() => {
    PriceInformationAlert({
      discountRate,
      representativePrice,
      kurlyPrice,
      originalPrice,
    });

    amplitudeService.logEvent(
      new SelectPriceTooltipButton({
        fusionQueryId: getFusionQueryId(queryId),
      }),
    );
  }, [discountRate, kurlyPrice, originalPrice, queryId, representativePrice]);

  return (
    <>
      <PriceWrapper>
        {discountRate > 0 && <DiscountPercent>{discountRate}%</DiscountPercent>}
        <PriceValue>{addComma(representativePrice)}</PriceValue>
        <PriceSubText>원{isMultiplePrice && '~'}</PriceSubText>
      </PriceWrapper>
      <SubPrice lineThrough={lineThrough} onClickPriceInformation={handleClickPriceInformation} subPrice={kurlyPrice} />
      <SubPrice onClickPriceInformation={handleClickPriceInformation} subPrice={originalPrice} />
    </>
  );
}
